import * as Icons from "@nova/component-icons"
import { JobStatusType, LayerIndex, SidePanelLayerType } from "@nova/constants"
import { SatelliteFlight } from "@nova/protobuf"
import {
    selectSatelliteFlightById,
    selectSatelliteFlights,
} from "features/entity-caching/flights/selectors"
import { zoomToSatellite } from "features/map/inspector-panel/satellite/module"
import {
    deleteSatelliteFlight,
    updateSatelliteFlight,
} from "features/pubsub/entityActions/flights/satellite"
import { getGenericActiveFeatureSidePanelComponent } from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { PluginRegistry } from "plugins/plugin-registry"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect } from "react"
import { setActiveFeatureId } from "panels/content/side-panel/slice.new"
import { useSatelliteLayer } from "./layers/satelliteLayer"
import { useSatelliteComparisonBoostLayer } from "./layers/useSatelliteComparisonBoostLayer"
import { useSatelliteOverlapLayer } from "./satelliteOverlapLayer"
import { selectAllSatelliteBoundingBoxes } from "./SatelliteSlice"
import { SatellitePluginActionBody } from "./SatellitePluginActionBody"
import { SatelliteProcessingPanel } from "./SatelliteProcessingPanel"
import { RegionSelectorZoomHint } from "./RegionSelector"
import { useSatelliteStatusAndProgress } from "panels/content/side-panel/progress/Satellite"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { getBadgeComponent } from "panels/content/side-panel/generics/GenericLayerListItemProgress"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelPlugin } from "features/map/inspector-panel/useInspectorPanel"
import { InspectorPanelSatelliteLayer } from "features/map/inspector-panel/satellite/InspectorPanelSatelliteLayer"
import { InspectorPanelSatelliteData } from "features/map/inspector-panel/satellite/InspectorPanelSatelliteData"
import { InspectorPanelOrthoComparisonPanel } from "features/map/inspector-panel/orthos/InspectorPanelOrthoComparisonPanel"
import { OrthoMaskPanel } from "features/map/inspector-panel/orthos/OrthoMaskPanel"
import { selectIsOrthoComparisonActive } from "plugins/Orthomosaic/OrthoComparisonSlice"
import { selectOrthoMaskEditingFlightId } from "plugins/Orthomosaic/OrthoMaskSlice"

export function registerSatellitePlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "satellite",
            displayName: "Satellite",
            actionsDisplayName: "Satellite Layer",
            sidePanelLayerType: SidePanelLayerType.SatelliteFlight,
            PrimaryIcon: Icons.Satellite,
            ImageIconUrl: "/images/plugins/layers/satellite.svg",
            updateAction: updateSatelliteFlight,
            deleteAction: deleteSatelliteFlight,
            confirmDelete: () => true,
            protobufMessage: SatelliteFlight,
            selectAllEntities: selectSatelliteFlights,
            selectSingleEntity: selectSatelliteFlightById,
            useUploadStatusAndProgress: useSatelliteStatusAndProgress,
            zoomTo: zoomToSatellite,
            duplicate: () => undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                RightSlotComponent: getBadgeComponent({
                    useStatusAndProgress: useSatelliteStatusAndProgress,
                }),
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => {
            const NormalSatellitePanel = getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelSatelliteLayer,
                DataComponent: InspectorPanelSatelliteData,
                isScrollable: true,
            })
            return {
                InspectorPanelComponent: ({
                    id,
                    plugin,
                }: {
                    id?: string
                    plugin: InspectorPanelPlugin
                }) => {
                    const comparisonIsActive = useAppSelector(
                        selectIsOrthoComparisonActive(id ?? "")
                    )
                    const editingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)
                    if (!id) return null
                    if (comparisonIsActive) return <InspectorPanelOrthoComparisonPanel />
                    if (editingFlightId === id) {
                        return <OrthoMaskPanel flightId={id} />
                    }
                    return <NormalSatellitePanel id={id} plugin={plugin} />
                },
            }
        })
        .implement("side-panel-active-feature-plugin", (spec) => {
            const CreateSatelliteFlightSidePanelComponent =
                getGenericActiveFeatureSidePanelComponent({
                    spec,
                    RenderComponent: SatellitePluginActionBody,
                })
            const ProcessingSatelliteFlightSidePanelComponent =
                getGenericActiveFeatureSidePanelComponent({
                    spec,
                    RenderComponent: SatelliteProcessingPanel,
                })
            return {
                // SatellitePluginActionBody always creates a new flight+job on confirm, so it must never render for an existing one.
                SidePanelComponent: ({ id }: { id?: string }) => {
                    const dispatch = useAppDispatch()
                    const entity = useAppSelector(spec.selectSingleEntity(id ?? ""))
                    const isExistingFlight = !!id && !!entity?.job
                    const isJobReady = entity?.job?.status
                        ? entity.job.status >= JobStatusType.COMPLETED
                        : false
                    const showNormalPanel = isExistingFlight && isJobReady
                    useEffect(() => {
                        if (showNormalPanel) {
                            dispatch(setActiveFeatureId({ feature: undefined }))
                        }
                    }, [dispatch, showNormalPanel])
                    if (showNormalPanel) return null
                    if (isExistingFlight) return <ProcessingSatelliteFlightSidePanelComponent />
                    return <CreateSatelliteFlightSidePanelComponent />
                },
            }
        })
        .implement("map-layer-plugin", () => ({
            useLayer: useSatelliteLayer,
            zIndex: LayerIndex.Satellite,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useSatelliteComparisonBoostLayer,
            zIndex: LayerIndex.OrthoComparisonBoost,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useSatelliteOverlapLayer,
            zIndex: LayerIndex.SatelliteOverlap,
        }))
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectAllSatelliteBoundingBoxes),
            showLabel: true,
        }))
        .implement("feature-plugin", () => ({
            sidePanelPluginActionsComponent: () => SatellitePluginActionBody(),
        }))
        .implement("map-modal-plugin", () => ({
            Modal: RegionSelectorZoomHint,
        }))
}
